import { useState, useEffect } from "react";
import { apiFetch, readBlobWithProgress, runInSession, BASE as API_BASE } from "../../../lib/api.js";
import { isImage, isPdf, isVideo, isAudio } from "../../../lib/utils.js";

export function humanSize(bytes) {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function fetchAttachmentBlob(id, onProgress) {
  return runInSession(async (signal) => {
    const res = await fetch(`${API_BASE}/attachments/${encodeURIComponent(id)}`, { credentials: 'include', signal });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return readBlobWithProgress(res, onProgress);
  });
}

export async function deleteAttachment(id) {
  await apiFetch(`/attachments/${encodeURIComponent(id)}`, { method: "DELETE" });
}

// A 403 here means either "not your attachment" or "no delete permission" — the
// backend's message (err.message, from body.detail) tells them apart, so surface
// that instead of a single hardcoded ownership-only string.
export function deleteAttachmentErrorMessage(err) {
  return err.status === 403
    ? err.message || "You don't have permission to delete this attachment."
    : "Could not delete the attachment.";
}

export async function downloadAttachment(a, onProgress) {
  const blob = a.blob ?? (await fetchAttachmentBlob(a.id, onProgress));
  const url = a.blob ? a.previewUrl : URL.createObjectURL(blob);
  if (a.blob) onProgress?.(1);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = a.filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  if (!a.blob) URL.revokeObjectURL(url);
}

export function getActivityText(ev) {
  return (
    ev.text ??
    ev.comment ??
    ev.body ??
    ev.message ??
    ev.content ??
    ev.note ??
    ""
  ).trim();
}

function adaptAttachment(a) {
  return {
    id: a.id,
    requestId: a.request_id ?? a.requestId ?? null,
    filename: a.filename ?? a.fileName ?? a.file_name ?? a.name ?? "download",
    contentType: a.content_type ?? a.contentType ?? "application/octet-stream",
    size: a.size ?? 0,
    createdAt: a.created_at ?? a.createdAt ?? null,
    by: a.by ?? a.uploaded_by ?? null,
    fromComment: a.from_comment ?? a.fromComment ?? false,
    failed: false,
  };
}

// Shared attachment-loading hook: adapts a raw list of attachment-like
// records, fetches each blob, and exposes loading state. Used by
// RequestDescriptionBlock, ActivityItem, and AttachmentsTab so the
// fetch/object-URL/cleanup logic exists in exactly one place.
export function useAttachments(rawList) {
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(false);

  const key = (rawList || []).map((x) => x?.id ?? "").join(",");

  useEffect(() => {
    if (!Array.isArray(rawList) || rawList.length === 0) {
      setAttachments([]);
      setLoading(false);
      return;
    }

    let alive = true;
    const createdUrls = [];
    setLoading(true);

    Promise.all(
      rawList.map(async (item) => {
        const adapted = adaptAttachment(item);
        if (!isImage(adapted.filename) && !isPdf(adapted.filename) && !isVideo(adapted.filename) && !isAudio(adapted.filename)) {
          return { ...adapted, previewUrl: null, blob: null, failed: false };
        }
        try {
          const blob = await fetchAttachmentBlob(adapted.id);
          const previewUrl = URL.createObjectURL(blob);
          createdUrls.push(previewUrl);
          return {
            ...adapted,
            size: blob.size || adapted.size,
            previewUrl,
            blob,
            failed: false,
          };
        } catch {
          return { ...adapted, previewUrl: null, blob: null, failed: true };
        }
      }),
    ).then((enriched) => {
      if (!alive) {
        createdUrls.forEach((u) => URL.revokeObjectURL(u));
        return;
      }
      setAttachments(enriched);
      setLoading(false);
    });

    return () => {
      alive = false;
      createdUrls.forEach((u) => URL.revokeObjectURL(u));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return { attachments, loading };
}
